/**
 * Trunnion AI - Workflow Graph Validation with Zod
 */

import { z } from 'zod';
import type { Node, Edge } from '@xyflow/react';
import type { WorkflowNodeData, WorkflowEdgeData } from '@/types';

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

const nodeSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  position: z.object({
    x: z.number(),
    y: z.number(),
  }),
  data: z.object({}).passthrough(),
});

const edgeSchema = z.object({
  id: z.string().min(1),
  source: z.string().min(1),
  target: z.string().min(1),
});

/**
 * Validate a workflow graph structure
 */
export function validateWorkflowGraph(
  nodes: Node<WorkflowNodeData>[],
  edges: Edge<WorkflowEdgeData>[],
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Basic schema validation
  for (const node of nodes) {
    const result = nodeSchema.safeParse(node);
    if (!result.success) {
      errors.push(`Invalid node "${node.id}": ${result.error.message}`);
    }
  }

  for (const edge of edges) {
    const result = edgeSchema.safeParse(edge);
    if (!result.success) {
      errors.push(`Invalid edge "${edge.id}": ${result.error.message}`);
    }
  }

  // Must have at least one node
  if (nodes.length === 0) {
    errors.push('Workflow must contain at least one node.');
    return { valid: false, errors, warnings };
  }

  // Must have exactly one trigger node
  const triggerNodes = nodes.filter((n) => n.type === 'trigger');
  if (triggerNodes.length === 0) {
    errors.push('Workflow must have a Trigger node as the entry point.');
  } else if (triggerNodes.length > 1) {
    errors.push('Workflow must have exactly one Trigger node.');
  }

  // Must have at least one terminal node
  const terminalNodes = nodes.filter((n) => n.type === 'terminal');
  if (terminalNodes.length === 0) {
    errors.push('Workflow must have at least one Terminal node.');
  }

  // Validate edge references
  const nodeIds = new Set(nodes.map((n) => n.id));
  for (const edge of edges) {
    if (!nodeIds.has(edge.source)) {
      errors.push(
        `Edge "${edge.id}" references non-existent source node "${edge.source}".`,
      );
    }
    if (!nodeIds.has(edge.target)) {
      errors.push(
        `Edge "${edge.id}" references non-existent target node "${edge.target}".`,
      );
    }
  }

  // Check for disconnected nodes
  const connectedNodes = new Set<string>();
  for (const edge of edges) {
    connectedNodes.add(edge.source);
    connectedNodes.add(edge.target);
  }

  for (const node of nodes) {
    if (!connectedNodes.has(node.id) && nodes.length > 1) {
      warnings.push(
        `Node "${(node.data as { label?: string })?.label || node.id}" is not connected to any other node.`,
      );
    }
  }

  // Check for cycles (simple DFS-based detection)
  const adjacency = new Map<string, string[]>();
  for (const edge of edges) {
    if (!adjacency.has(edge.source)) {
      adjacency.set(edge.source, []);
    }
    adjacency.get(edge.source)!.push(edge.target);
  }

  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  function hasCycle(nodeId: string): boolean {
    visited.add(nodeId);
    recursionStack.add(nodeId);

    const neighbors = adjacency.get(nodeId) ?? [];
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        if (hasCycle(neighbor)) return true;
      } else if (recursionStack.has(neighbor)) {
        return true;
      }
    }

    recursionStack.delete(nodeId);
    return false;
  }

  for (const node of nodes) {
    if (!visited.has(node.id)) {
      if (hasCycle(node.id)) {
        errors.push(
          'Workflow contains a cycle. Cyclic workflows are not supported.',
        );
        break;
      }
    }
  }

  // Parallel fork/join matching
  const forkNodes = nodes.filter((n) => n.type === 'parallelFork');
  const joinNodes = nodes.filter((n) => n.type === 'parallelJoin');
  if (forkNodes.length !== joinNodes.length) {
    warnings.push(
      `Mismatched parallel fork/join nodes: ${forkNodes.length} forks vs ${joinNodes.length} joins.`,
    );
  }

  // Check trigger has no incoming edges
  for (const trigger of triggerNodes) {
    const incomingEdges = edges.filter((e) => e.target === trigger.id);
    if (incomingEdges.length > 0) {
      errors.push('Trigger node must not have any incoming edges.');
    }
  }

  // Check terminal has no outgoing edges
  for (const terminal of terminalNodes) {
    const outgoingEdges = edges.filter((e) => e.source === terminal.id);
    if (outgoingEdges.length > 0) {
      errors.push('Terminal node must not have any outgoing edges.');
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate agent node data
 */
export function validateAgentNode(
  data: Record<string, unknown>,
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!data.agentId) {
    errors.push('Agent node must have an assigned agent.');
  }

  if (!data.label || (data.label as string).trim() === '') {
    errors.push('Agent node must have a label.');
  }

  const timeout = data.timeoutSeconds as number | undefined;
  if (timeout !== undefined && timeout <= 0) {
    errors.push('Agent timeout must be greater than 0.');
  }

  if (timeout !== undefined && timeout > 3600) {
    warnings.push('Agent timeout exceeds 1 hour. Consider reducing for safety.');
  }

  return { valid: errors.length === 0, errors, warnings };
}
